// The SF DocAI Apex wrapper serialises nested fields as toString() strings
// rather than proper JSON. Two formats can appear in a field's `value`:
//   Scalar:  some plain string
//   Object:  {key=value, key2={nested=val}, ...}
//   List:    ({...}, {...}, ...)   ← array of objects
// Shared by the admin DocAI testing page and the form mapper.
// (api/docai.ts).

export interface ApexSubField {
  name: string
  value: string
  confidence?: number
}

// Splits `inner` by commas that are at brace-depth 0.
export function splitByComma(inner: string): string[] {
  const parts: string[] = []
  let depth = 0
  let current = ''
  for (const ch of inner) {
    if (ch === '{') depth++
    else if (ch === '}') depth--
    if (ch === ',' && depth === 0) {
      parts.push(current.trim())
      current = ''
    } else {
      current += ch
    }
  }
  if (current.trim()) parts.push(current.trim())
  return parts
}

// Parses {key=value, ...} → plain object. Returns null if not an Apex object string.
export function parseApexObject(s: string): Record<string, string> | null {
  s = s.trim()
  if (!s.startsWith('{') || !s.endsWith('}')) return null
  const result: Record<string, string> = {}
  for (const part of splitByComma(s.slice(1, -1))) {
    const eq = part.indexOf('=')
    if (eq !== -1) result[part.slice(0, eq).trim()] = part.slice(eq + 1).trim()
  }
  return result
}

// Parses ({...}, {...}, ...) → array of sub-field rows.
// Each item has shape {type=object, value={FieldName={confidence_score=N, type=string, value=V}, ...}}
export function parseApexList(s: string): ApexSubField[][] | null {
  s = s.trim()
  if (!s.startsWith('(') || !s.endsWith(')')) return null
  const inner = s.slice(1, -1).trim()
  if (!inner) return []

  // Collect top-level {…} blocks (items are separated by ", " between closing and opening braces).
  const rows: ApexSubField[][] = []
  let depth = 0
  let current = ''
  for (const ch of inner) {
    if (ch === '{') depth++
    else if (ch === '}') depth--
    current += ch
    if (depth === 0 && current.trim()) {
      const item = parseApexObject(current.trim())
      if (item) rows.push(parseApexItemFields(item))
      current = ''
    }
  }
  return rows.length > 0 ? rows : null
}

// Extracts sub-fields from a single list item object.
// Item shape: { type: 'object', value: '{First Name={confidence_score=0.94, type=string, value=Li Cheng}, ...}' }
export function parseApexItemFields(item: Record<string, string>): ApexSubField[] {
  const valueObj = parseApexObject(item['value'] ?? '')
  if (!valueObj) return []
  return Object.entries(valueObj).map(([name, raw]) => {
    const sub = parseApexObject(raw)
    if (!sub) return { name, value: raw }
    const conf = parseFloat(sub['confidence_score'] ?? '')
    return { name, value: sub['value'] ?? raw, confidence: isNaN(conf) ? undefined : conf }
  })
}

// Returns true when a field value is an Apex list string.
export function isApexList(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const s = value.trim()
  return s.startsWith('(') && s.endsWith(')')
}

// Normalises the string "null" (SF's missing-value sentinel) to undefined.
export function normaliseValue(value: string | null | undefined): string | undefined {
  if (value == null || value === 'null') return undefined
  return value
}
